import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactGA from "react-ga4";
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { paymentService } from '../services/paymentService';
import { useSEO } from '../hooks/useSEO';

const CartPage = () => {
  useSEO({
    title: "Shopping Cart - Complete Your Order | AM CARDMATE",
    description: "Review your selected NFC cards and digital solutions. Secure checkout with fast delivery options.",
    image: "/assets/cart.jpg",
    keywords: "shopping cart, checkout, buy NFC cards, order, purchase"
  });
  
  const { cart, loading, updateCartItem, removeFromCart, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'GB'
  });

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      ReactGA.event({
        category: "E-commerce",
        action: "Remove from Cart",
        label: itemId
      });
      removeFromCart(itemId);
    } else {
      ReactGA.event({
        category: "E-commerce",
        action: "Update Quantity",
        label: itemId,
        value: newQuantity
      });
      updateCartItem(itemId, newQuantity);
    }
  };

  const handleCheckout = () => {
    if (!user) {
      ReactGA.event({
        category: "E-commerce",
        action: "Checkout Attempt",
        label: "Not Logged In"
      });
      navigate('/login', { state: { from: { pathname: '/cart' } } });
      return;
    }

    ReactGA.event({
      category: "E-commerce",
      action: "Proceed to Payment",
      value: cart.total
    });

    navigate('/payment', {
      state: {
        orderData: {
          shippingInfo: {
            ...shippingInfo,
            firstName: shippingInfo.firstName || user.displayName?.split(' ')[0] || '',
            lastName: shippingInfo.lastName || user.displayName?.split(' ')[1] || '',
            email: user.email
          }
        }
      }
    });
  };

  const handleClearCart = () => {
    ReactGA.event({
      category: "E-commerce",
      action: "Clear Cart",
      value: cart.total
    });
    clearCart();
    setShowClearModal(false);
  };

  const countries = paymentService.getShippingCountries();

  // Not logged in state
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
        
        <div className="max-w-md w-full text-center relative z-10">
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <FontAwesomeIcon icon="shopping-cart" className="text-white text-3xl" />
            </div>
            <h2 className="text-h4 text-dark mb-4">Sign in to view your cart</h2>
            <p className="text-body text-gray-600 mb-6">
              Please sign in to save items to your cart and continue shopping.
            </p>
            <div className="space-y-3">
              <Link to="/login" className="btn btn-primary w-full">
                <FontAwesomeIcon icon="sign-in-alt" />
                <span>Sign In</span>
              </Link>
              <Link to="/signup" className="btn btn-outline w-full">
                <FontAwesomeIcon icon="user-plus" />
                <span>Create Account</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon="spinner" className="text-4xl text-blue-500 animate-spin mb-4" />
          <p className="text-body text-gray-600">Loading your cart...</p>
        </div>
      </div>
    );
  }

  // Empty cart state
  if (cart.items.length === 0) {
    ReactGA.event({
      category: "E-commerce",
      action: "View Empty Cart"
    });
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
        
        <div className="max-w-md w-full text-center relative z-10">
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <FontAwesomeIcon icon="shopping-cart" className="text-white text-3xl" />
            </div>
            <h2 className="text-h4 text-dark mb-4">Your cart is empty</h2>
            <p className="text-body text-gray-600 mb-6">
              Looks like you haven't added any items to your cart yet. Explore our premium NFC cards!
            </p>
            <div className="space-y-3">
              <Link to="/nfc-cards" className="btn btn-primary w-full">
                <FontAwesomeIcon icon="credit-card" />
                <span>Shop NFC Cards</span>
              </Link>
              <Link to="/custom-cards" className="btn btn-outline w-full">
                <FontAwesomeIcon icon="palette" />
                <span>Custom Cards</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  ReactGA.event({
    category: "E-commerce",
    action: "View Cart",
    value: cart.total
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 sm:py-12 px-4 sm:px-6">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
      
      <div className="max-w-7xl mx-auto relative z-10 pt-20 md:pt-24 lg:pt-32">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-h2 text-dark mb-4">
            Your <span className="text-gradient-blue-pink">Shopping Cart</span>
          </h1>
          <p className="text-body text-gray-600">
            {cart.items.reduce((count, item) => count + item.quantity, 0)} items in your cart
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, index) => (
              <div key={`${item.id}-${item.type}-${index}`} className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  {/* Item Image */}
                  <div className="w-full sm:w-24 md:w-32 h-24 md:h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center flex-shrink-0">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-contain rounded-2xl"
                      />
                    ) : (
                      <FontAwesomeIcon icon="credit-card" className="text-2xl md:text-4xl text-gray-400" />
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 w-full">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-card-title text-dark mb-1">{item.name}</h3>
                        <p className="text-body-sm text-gray-600 mb-2">{item.description || item.tagline}</p>
                        {item.material && (
                          <p className="text-caption text-gray-500">Material: {item.material}</p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          ReactGA.event({
                            category: "E-commerce",
                            action: "Remove from Cart",
                            label: item.name,
                            value: item.price
                          });
                          removeFromCart(item.id);
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-xl transition-all duration-300 mt-2 sm:mt-0 self-start"
                        title="Remove item"
                      >
                        <FontAwesomeIcon icon="trash" />
                      </button>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-3">
                        <span className="text-body-sm font-medium text-gray-700">Qty:</span>
                        <div className="flex items-center gap-1 bg-gray-100/70 rounded-2xl p-1">
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            className="w-8 h-8 rounded-xl bg-white flex items-center justify-center text-gray-600 hover:text-gray-800 hover:shadow-md transition-all duration-300"
                          >
                            <FontAwesomeIcon icon="minus" className="text-xs" />
                          </button>
                          <span className="w-10 text-center text-body font-medium">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            className="w-8 h-8 rounded-xl bg-white flex items-center justify-center text-gray-600 hover:text-gray-800 hover:shadow-md transition-all duration-300"
                          >
                            <FontAwesomeIcon icon="plus" className="text-xs" />
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <div className="text-h5 text-dark">
                          £{(item.price * item.quantity).toFixed(2)}
                        </div>
                        <div className="text-body-sm text-gray-500">
                          £{item.price.toFixed(2)} each
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Clear Cart Button */}
            <div className="text-center">
              <button
                onClick={() => setShowClearModal(true)}
                className="text-body-sm text-red-500 hover:text-red-700 hover:bg-red-50 px-4 py-2 rounded-2xl font-medium transition-all duration-300"
              >
                <FontAwesomeIcon icon="trash" className="mr-1" />
                Clear entire cart
              </button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-4 sm:p-6 sticky top-6">
              <h3 className="text-h5 text-dark mb-6">Order Summary</h3>
              
              {/* Summary Details */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-body text-gray-600">Subtotal</span>
                  <span className="text-body font-medium">£{cart.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-body text-gray-600">Shipping</span>
                  <span className="text-body font-medium text-blue-600">Calculated at checkout</span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-h5">
                    <span>Estimated Total</span>
                    <span className="text-gradient-blue-pink">
                      £{cart.total.toFixed(2)}+
                    </span>
                  </div>
                  <p className="text-caption text-gray-500 mt-1">Final total includes shipping</p>
                </div>
              </div>

              {/* Free Shipping Notice */}
              {cart.total >= 50 && (
                <div className="bg-green-50 rounded-2xl p-3 mb-4 border border-green-200">
                  <p className="text-body-sm text-green-700 font-medium">
                    🎉 You may qualify for FREE shipping!
                  </p>
                  <p className="text-caption text-green-600 mt-1">
                    Free shipping on £50+ orders to Europe
                  </p>
                </div>
              )}

              {/* Checkout Button */}
              <button
                onClick={() => {
                  ReactGA.event({
                    category: "E-commerce",
                    action: "Initiate Checkout",
                    value: cart.total
                  });
                  setShowCheckout(true);
                }}
                className="w-full btn btn-primary mb-4"
              >
                <FontAwesomeIcon icon="credit-card" />
                <span>Proceed to Checkout</span>
              </button>

              {/* Continue Shopping */}
              <Link to="/nfc-cards" className="w-full btn btn-outline mb-6">
                <FontAwesomeIcon icon="arrow-left" />
                <span>Continue Shopping</span>
              </Link>

              {/* Trust Badges */}
              <div className="pt-6 border-t border-gray-200">
                <div className="space-y-3 text-body-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon="shield-alt" className="text-green-500" />
                    <span>Secure SSL checkout</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon="truck" className="text-blue-500" />
                    <span>Free shipping on £50+ (Europe)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon="headset" className="text-purple-500" />
                    <span>24/7 customer support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clear Cart Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="exclamation-triangle" className="text-2xl text-orange-600" />
              </div>
              <h3 className="text-h5 text-dark mb-2">Clear Cart</h3>
              <p className="text-body-sm text-gray-600">
                Are you sure you want to remove all items from your cart? This action cannot be undone.
              </p>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-orange-50 rounded-2xl border border-orange-200">
                <h4 className="text-body font-semibold text-orange-800 mb-2">Items to be removed:</h4>
                <ul className="text-body-sm text-orange-700 space-y-1">
                  {cart.items.map((item, index) => (
                    <li key={index}>• {item.name} (Qty: {item.quantity})</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleClearCart}
                className="flex-1 btn bg-red-600 text-white hover:bg-red-700"
              >
                <FontAwesomeIcon icon="trash" />
                <span>Yes, Clear Cart</span>
              </button>
              <button
                onClick={() => setShowClearModal(false)}
                className="flex-1 btn btn-outline"
              >
                Keep Items
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-h5 text-dark">Shipping Information</h3>
              <button
                onClick={() => setShowCheckout(false)}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 p-2 rounded-xl transition-all duration-300"
              >
                <FontAwesomeIcon icon="times" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleCheckout(); }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-body-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={shippingInfo.firstName}
                    onChange={(e) => setShippingInfo({...shippingInfo, firstName: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-body-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={shippingInfo.lastName}
                    onChange={(e) => setShippingInfo({...shippingInfo, lastName: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-body-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  required
                  value={shippingInfo.address}
                  onChange={(e) => setShippingInfo({...shippingInfo, address: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body"
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-body-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    required
                    value={shippingInfo.city}
                    onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body"
                    placeholder="London"
                  />
                </div>

                <div>
                  <label className="block text-body-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    required
                    value={shippingInfo.postalCode}
                    onChange={(e) => setShippingInfo({...shippingInfo, postalCode: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body"
                    placeholder="SW1A 1AA"
                  />
                </div>
              </div>

              <div>
                <label className="block text-body-sm font-medium text-gray-700 mb-1">
                  Country *
                </label>
                <select
                  value={shippingInfo.country}
                  onChange={(e) => setShippingInfo({...shippingInfo, country: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body"
                  required
                >
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="border-t border-gray-200 pt-4 mt-6">
                <div className="flex justify-between text-h5 mb-4">
                  <span>Estimated Total: £{cart.total.toFixed(2)}+</span>
                </div>
                <p className="text-body-sm text-gray-600 mb-4">
                  Shipping cost will be calculated at checkout based on your location.
                </p>
                
                <div className="space-y-3">
                  <button
                    type="submit"
                    className="w-full btn btn-primary"
                  >
                    <FontAwesomeIcon icon="arrow-right" />
                    <span>Continue to Payment</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setShowCheckout(false)}
                    className="w-full btn btn-outline"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;